import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Button, Stack, Switch, Typography } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import SearchField from "@entrilia/shared/components/inputs/SearchField.tsx";
import type { DimensionDescriptor, DimensionDictionary } from "../../../../../../api/types/svTypes.ts";
import FieldPopover from "../../../sv/components/viewer/common/fields/FieldPopover.tsx";
import DimensionValueGrid from "./DimensionValueGrid.tsx";

interface Props {
  dimension: DimensionDescriptor;
  values: DimensionDictionary[];
  loading: boolean;
  selectedValues: string[];
  onSelectedValuesChange: (values: string[]) => void;
}

const DimensionValuePicker = ({ dimension, values, loading, selectedValues, onSelectedValuesChange }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [textFilter, setTextFilter] = useState("");
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  const handleOpen = useCallback(() => {
    setAnchorEl(buttonRef.current ?? undefined);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(undefined);
  }, []);

  const selectedCount = selectedValues.length;
  const displayField = dimension.availableFields[1] ?? dimension.keyFieldName;
  const selectedNames = selectedValues.map((value) =>
    String(values.find((v) => v[dimension.keyFieldName] === value)?.[displayField] ?? value)
  );
  const buttonLabel = selectedCount > 0 ? selectedNames.join(", ") : "Select values...";

  return (
    <>
      <Button
        ref={buttonRef}
        variant="text"
        color="secondary"
        onClick={handleOpen}
        endIcon={<ChevronRightRoundedIcon />}
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          fontWeight: 400,
          px: 1.5,
          py: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <Typography variant="body2" color={selectedCount > 0 ? "primary" : "textDisabled"} noWrap>
          {buttonLabel}
        </Typography>
      </Button>
      <FieldPopover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        sx={{ display: "flex", flexDirection: "column", p: 0, width: "370px", height: "auto" }}
      >
        <Stack sx={{ p: 2, gap: 1.5, flex: 1 }}>
          <Stack direction="row" sx={{ alignItems: "center", pl: 0.5 }}>
            <Typography>Show only selected</Typography>
            <HorizontalFill />
            <Switch checked={showOnlySelected} onChange={(_, value) => setShowOnlySelected(value)} />
          </Stack>
          <SearchField onSearch={setTextFilter} debounceTimeMs={500} fullWidth />
          <DimensionValueGrid
            dimension={dimension}
            values={values}
            loading={loading}
            selectedValues={selectedValues}
            showOnlySelected={showOnlySelected}
            textFilter={textFilter}
            onSelectionChange={onSelectedValuesChange}
          />
        </Stack>
      </FieldPopover>
    </>
  );
};

export default DimensionValuePicker;
